import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useExperimentApi } from '../../store/api/hooks/experimentApiHook'
import { useDialog } from '../../store/api/hooks/dialogHook'
import { BusinessFunctionDataType } from '../../_config/types'
import { ErrorResponse } from '../../store/api/apiTypes'
import { cellRenderer } from '../../components/cellRenderer'
import { URLS } from '../../_config'
import { normalizeRowValues, trackEvent } from '../functions'
import { startTiming, stopTiming } from '../../utils/analytics/rumTracking'

export interface MeasurementConfigureOptions {
    experimentType: 'RCT' | 'OSTUDY'
    groupValidation?: {
        minGroups?: number
        maxGroups?: number
        exactGroups?: number
    }
    blockingFactorsConfig?: {
        isRequired?: boolean
        maxCount?: number
        label?: string
        description?: string
    }
}

// Shared API functions that can be used by both hook and component
export const createSharedMeasurementServices = (experimentApi: any, commonApi: any, params: any, experiment: any) => {
    const createColumnDef = (result: any) => {
        return result.length > 0
            ? Object.keys(result[0]).map((key: any) => ({
                  headerName: key,
                  field: key,
                  filter: false,
                  sortable: false,
                  suppressMenu: true,
                  cellStyle: {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                  },
                  cellRenderer,
              }))
            : []
    }

    // Shared function for getting table data
    const getTableData = (tableType: 'KPI' | 'Recommendation' = 'KPI', expLiteral: string = params.experiment) => {
        return new Promise((resolve, reject) => {
            let payload: any = {
                literal: expLiteral,
                page: 1,
                per_page: 40,
                table_type: tableType,
            }

            // Get appropriate filters based on table type
            const filterKey = tableType === 'KPI' ? 'measurement_table_filters' : 'recommendation_table_filters'
            const filters = experiment.experiment_metadatas?.[filterKey]

            if (filters?.filters && Object.keys(filters.filters).length) {
                payload = { ...payload, filters: filters.filters }
            } else if (filters?.sql_query) {
                payload = { ...payload, sql_query: filters.sql_query }
            }

            experimentApi.getFilterPreviwData(
                payload,
                (data: any) => {
                    const rawResult = data?.data?.result || []
                    const result = rawResult.filter((row: any) => row && typeof row === 'object').map(normalizeRowValues)
                    const columnDefs = createColumnDef(result)
                    resolve({
                        rowData: result,
                        columnDefs,
                    })
                },
                (error: any) => {
                    reject(new Error(error?.message || 'An unknown error occurred'))
                },
            )
        })
    }

    const createKPITypes = (data: any) => {
        return structuredClone(data)?.map((kpi: any) => ({
            ...kpi,
            label: kpi.name,
            value: kpi.kpi_literal,
        }))
    }

    // Shared function for getting KPI types
    const getKpiTypes = () => {
        return new Promise((resolve, reject) => {
            commonApi.getKpiTypes(
                { sort_by: '-created_at', page: 1, per_page: 10 },
                (data: any) => {
                    const interests = createKPITypes(data.data.result)
                    resolve(interests)
                },
                (error: ErrorResponse<BusinessFunctionDataType>) => {
                    reject(new Error(error?.message || 'An unknown error occurred'))
                },
            )
        })
    }

    const ExecuteAnalysis = (resolve: any, reject: any, expLiteral: string = params.experiment) => {
        experimentApi.executemeasurmentanalysis(
            { literal: expLiteral },
            (resp: any) => {
                resolve(resp)
            },
            (error: any) => {
                reject(error)
            },
        )
    }

    const ExecuteOverview = (resolve: any, reject: any, expLiteral: string = params.experiment) => {
        experimentApi.executedesignmergeoverview(
            { literal: expLiteral },
            (resp: any) => {
                ExecuteAnalysis(resolve, reject, expLiteral)
            },
            (error: any) => {
                reject(error)
            },
        )
    }

    // Shared function for updating measurement experiment
    const updateMuserementExperiment = (formData: any, options: MeasurementConfigureOptions, expLiteral: string = params.experiment) => {
        return new Promise((resolve, reject: any) => {
            let control: boolean = false

            const group_configs = formData.groupConfigs.map((group: any) => {
                if (group.isControl) {
                    control = true
                }
                return {
                    name: group.name,
                    action: group.action,
                    is_control: group.isControl,
                    group_allocation_percentage: Number.parseFloat(group.distribution),
                    group_size: group.size,
                }
            })

            if (!control) {
                reject(new Error('Please select a group as your control group'))
                return
            }

            const payload: any = {
                literal: expLiteral,
                step: 6,
                is_draft: false,
                group_configs,
                interventions: [
                    {
                        ...experiment.experiment_metadatas?.interventions[0],
                        group_configs,
                    },
                ],
            }

            experimentApi.updateExperiment(
                payload,
                (resp: any) => {
                    ExecuteOverview(resolve, reject, expLiteral)
                },
                (error: any) => {
                    reject(new Error(error?.message || 'An unknown error occurred'))
                },
            )
        })
    }

    // Shared function for getting columns
    const getColumns = (tableType: 'KPI' | 'Recommendation' = 'KPI', expLiteral: string = params.experiment) => {
        return new Promise((resolve, reject) => {
            experimentApi.getColumnsForGroupDristribution(
                {
                    literal: expLiteral,
                    tableType,
                },
                (resp: any) => {
                    resolve(resp.data)
                },
                (error: any) => {
                    reject(new Error(error?.message || 'An unknown error occurred'))
                },
            )
        })
    }

    return {
        getTableData,
        getKpiTypes,
        updateMuserementExperiment,
        getColumns,
    }
}

export const useMeasurementConfigure = (options: MeasurementConfigureOptions): any => {
    const params: any = useParams()
    const experimentApi = useExperimentApi()
    const dialog = useDialog()
    const navigate: any = useNavigate()

    const getExperimentDetails = (expLiteral: string = params.experiment) => {
        return new Promise((resolve: any, reject: any) => {
            experimentApi.getExperimentDetailsWatch(
                { literal: expLiteral + '?use_orm=true' },
                (resp: any) => {
                    resolve(resp)
                },
                (error: any) => {
                    resolve(null)
                },
            )
        })
    }

    const callMeasure = (trigger: any, update: any) => {
        if (trigger == null) {
            update()
        } else {
            trigger()
                .then((valid: boolean) => {
                    if (valid) {
                        update()
                    }
                })
                .catch((error: any) => {
                    console.error('Error :', error)
                })
        }
    }

    const checkBeforeMeasurementStart = (trigger: any, update: any, expLiteral: string = params.experiment) => {
        void getExperimentDetails(expLiteral).then((experiment: any) => {
            if (experiment?.is_measurement_triggered && !experiment?.is_completed) {
                trackEvent('measurement_resubmit_warning_shown', { experiment_literal: expLiteral })
                dialog.confirm({
                    type: 'delete',
                    title: 'Measurement In Progress',
                    message: `Are you sure you want to re-submit this experiment? This action will overwrite the existing experiment.`,
                    warning: `This action cannot be undone.`,
                    confirmButtonLabel: 'Yes Continue',
                    onConfirm: () => {
                        trackEvent('measurement_resubmit_confirmed', { experiment_literal: expLiteral })
                        callMeasure(trigger, update)
                    },
                    onCancel: () => {
                        trackEvent('measurement_resubmit_cancelled', { experiment_literal: expLiteral })
                    },
                })
            } else if (experiment?.is_completed) {
                deleteInsightsWarning(() => {
                    callMeasure(trigger, update)
                })
                /*  trackEvent('measurement_completed_info_shown', { experiment_literal: expLiteral })
                 dialog.confirm({
                     type: 'delete',
                     title: 'Complete Experiment',
                     message: `Experiment has already been completed.`,
                     warning: `The user is informed that the experiment is no longer active for analysis.`,
                     confirmButtonLabel: 'ok I Got It',
                     onConfirm: () => {},
                 }) -- back up */
            } else {
                callMeasure(trigger, update)
            }
        })
    }

    const updateExperimentAfterDeleteInsights = (expLiteral: string = params.experiment) => {
        return new Promise((resolve, reject) => {
            experimentApi.updateExperiment(
                {
                    literal: expLiteral,
                    step: 5,
                    is_draft: true,
                    is_rerun: true,
                },
                (resp: any) => {
                    getExperimentDetails(expLiteral).then(resolve).catch(reject)
                },
                (error: any) => {
                    console.log(error)
                },
            )
        })
    }

    const deleteInsights = (expLiteral: string = params.experiment) => {
        return new Promise((resolve, reject) => {
            experimentApi.deleteInsights(
                { literal: expLiteral },
                (resp: any) => {
                    updateExperimentAfterDeleteInsights(expLiteral).then(resolve).catch(reject)
                    toast.success('Insights deleted successfully')
                },
                (error: any) => {
                    toast.error(error?.message)
                },
            )
        })
    }

    const deleteInsightsWarning = (confirmAction: any, expLiteral: string = params.experiment) => {
        trackEvent('measurement_insights_delete_warning_shown', { experiment_literal: expLiteral })
        dialog.confirm({
            type: 'delete',
            title: 'Measurement Configuration Changed',
            message: '',
            warning: 'You have changed measurement configurations, the results will be overwritten.',
            confirmButtonLabel: 'Yes Proceed',
            onConfirm: () => {
                trackEvent('measurement_insights_delete_confirmed', { experiment_literal: expLiteral })
                deleteInsights(expLiteral)
                    .then((resp: any) => {
                        confirmAction()
                    })
                    .catch((err) => {
                        console.log(err)
                    })
            },
            onCancel: () => {
                trackEvent('measurement_insights_delete_cancelled', { experiment_literal: expLiteral })
                console.log('No')
            },
        })
    }

    const getTableData = (experiment: any, type: 'KPI' | 'Intervention' = 'KPI') => {
        return new Promise((resolve, reject) => {
            let payload = {
                literal: experiment.experiment_literal,
                page: 1,
                per_page: 40,
                table_type: type,
            }

            if (type == 'KPI') {
                if (experiment.experiment_metadatas?.measurement_table_filters?.filters && Object.keys(experiment.experiment_metadatas?.measurement_table_filters?.filters).length) {
                    const F = { filters: experiment.experiment_metadatas?.measurement_table_filters?.filters }
                    payload = { ...payload, ...F }
                } else if (experiment.experiment_metadatas?.measurement_table_filters?.sql_query) {
                    const S = { sql_query: experiment.experiment_metadatas?.measurement_table_filters?.sql_query }
                    payload = { ...payload, ...S }
                }
            }
            if (type == 'Intervention') {
                if (experiment.experiment_metadatas?.intervention_table_filters?.filters && Object.keys(experiment.experiment_metadatas?.intervention_table_filters?.filters).length) {
                    const F = { filters: experiment.experiment_metadatas?.intervention_table_filters?.filters }
                    payload = { ...payload, ...F }
                } else if (experiment.experiment_metadatas?.intervention_table_filters?.sql_query) {
                    const S = { sql_query: experiment.experiment_metadatas?.intervention_table_filters?.sql_query }
                    payload = { ...payload, ...S }
                }
            }

            experimentApi.getFilterPreviwData(
                payload,
                (data: any) => {
                    const rawResult = data?.data?.result ?? []
                    const result = rawResult.filter((row: any) => row && typeof row === 'object').map(normalizeRowValues)
                    resolve(result)
                },
                (error: any) => {
                    reject(new Error(error?.message || 'An unknown error occurred'))
                },
            )
        })
    }

    const executeMeasurement = (expLiteral: string = params.experiment) => {
        return new Promise((resolve, reject) => {
            startTiming('rct_execution')
            experimentApi.executemeasurmentanalysis(
                { literal: expLiteral },
                (resp: any) => {
                    stopTiming('rct_execution', { experiment_literal: expLiteral ?? '', experiment_type: options?.experimentType ?? '' })
                    resolve(resp)
                    toast.success('Measurement Data Successfully Added.')
                    navigate(URLS.MY_EXPERIMENTS)
                },
                (error: any) => {
                    toast.error(error?.message)
                    reject(error)
                },
            )
        })
    }

    const getMeasurementConfigPayload = (getValues: any, experiment: any, expLiteral: string = params.experiment) => {
        const DATA: any = getValues()
        let control: boolean = false
        const group_configs = DATA.groupConfigs.map((group: any) => {
            if (group.isControl) {
                control = true
            }
            return {
                name: group.name,
                action: group.action,
                is_control: group.isControl,
                group_allocation_percentage: Number.parseFloat(group.distribution),
                group_size: group.size,
            }
        })

        if (!control) {
            toast.error('Please select a group as your control group')
            return null
        }

        return {
            literal: expLiteral,
            step: 6,
            is_draft: false,
            group_configs,
            interventions: [
                {
                    ...experiment.experiment_metadatas?.interventions[0],
                    group_configs,
                },
            ],
            measurement_column_map: {
                granularity: DATA._name1.value,
                date: DATA?._name2?.value,
                allocation_group: DATA._name3?.value,
                blocking_factors: DATA.blockingFactors?.map((option: any) => option.value),
                date_frequency: DATA.frequency,
                kpi: DATA.metrics.map((kpi: any) => {
                    const kpidata = {
                        ...kpi,
                        column_map: kpi.column_map.value,
                    }
                    delete kpidata.id
                    return kpidata
                }),
            },
            submit_from: 'measurement_configuration',
        }
    }

    return {
        checkBeforeMeasurementStart,
        deleteInsightsWarning,
        getTableData,
        executeMeasurement,
        getMeasurementConfigPayload,
    }
}
